import React, {useState} from 'react';
import {Redirect, Switch} from 'react-router-dom';
import {VersionReloadRoute} from '../../containers/version_reload_route';
import {SecondaryNavStateProvider} from '../navigation/secondary/SecondaryNavigationStateManager';
import ClientSecondaryNavigation from './ClientSecondaryNavigation';
import CompanyViewPage from './CompanyViewPage';
import CompanyViewOpportunities from './CompanyViewOpportunities';
import {CompanyViewWorkspace} from './CompanyViewWorkspace';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {hasModule} from '../shared/util/ModuleUtil';
import {isCustomerPortalAvailable} from '../shared/util/customerPortalAvailability';
import {FEATURE_FLAGS, MODULE_TYPES} from '../../constants';

type Props = {
	clientId: string;
};

// Route-level shell for the client/company view: a persistent secondary navigation alongside a
// content area that swaps between client sub-sections. Mirrors ProjectRoutes. Today the only
// section is the Overview; future sections are added as additional routes inside the Switch.
const CompanyViewRoutes: React.FC<Props> = ({clientId}) => {
	const [currentPath, updateCurrentPath] = useState(window.location.pathname + window.location.hash);

	// The Workspace section is gated on portal availability (module OR flag). When off the route is not
	// registered, so a stale bookmark falls through to the base-path redirect.
	const showWorkspace = isCustomerPortalAvailable();

	// The Opportunities section ships dark behind the feature flag + module; when off the route is not
	// registered, so a direct /clients/:clientId/opportunities hit falls through to the base-path redirect.
	const showOpportunities = hasFeatureFlag(FEATURE_FLAGS.OPPORTUNITIES) && hasModule(MODULE_TYPES.OPPORTUNITIES);

	return (
		<SecondaryNavStateProvider>
			<div style={{flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'row'}}>
				<ClientSecondaryNavigation
					clientId={clientId}
					currentPath={currentPath}
					updateCurrentPath={updateCurrentPath}
				/>
				<div style={{flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto'}}>
					<Switch>
						{showWorkspace && (
							<VersionReloadRoute
								exact
								path="/clients/:clientId/workspace"
								children={({match}) => {
									const id = match.params.clientId;
									if (!id) return null;
									return <CompanyViewWorkspace clientId={id} />;
								}}
							/>
						)}
						{showOpportunities && (
							<VersionReloadRoute
								exact
								path="/clients/:clientId/opportunities"
								children={({match}) => {
									const id = match.params.clientId;
									if (!id) return null;
									return <CompanyViewOpportunities clientId={id} />;
								}}
							/>
						)}
						<VersionReloadRoute
							exact
							path="/clients/:clientId"
							children={({match}) => {
								const id = match.params.clientId;
								if (!id) return null;
								return <CompanyViewPage clientId={id} />;
							}}
						/>
						{/* Any unmatched client sub-path (e.g. a stale /opportunities bookmark while the section is
						    gated off) falls back to the client Overview rather than rendering a blank content area. */}
						<Redirect to={`/clients/${clientId}`} />
					</Switch>
				</div>
			</div>
		</SecondaryNavStateProvider>
	);
};

export default CompanyViewRoutes;
